/**
 * Weapon Class
 *
 * Extends Item to add weapon-specific functionality
 * Handles firing, reloading, damage, and projectile behavior
 */

import { Item, ItemType, ItemRarity } from './Item.js';
import { spreadOffsets } from './ShotModel.js';

// Weapon firing modes
const WeaponFireMode = {
  SEMI_AUTO: 'semi_auto',      // One shot per click
  BURST: 'burst',               // Fixed burst of shots
  FULL_AUTO: 'full_auto',       // Continuous fire while held
  MELEE: 'melee'                // Swung, not fired — handled by MeleeSystem, never WeaponSystem
};

/**
 * Cone pellet offset in radians. Two uniform rolls averaged into a triangular
 * fan: peaked on the aim line, still able to reach ±pelletSpreadDegrees.
 * @param {number} pelletSpreadDegrees
 * @param {() => number} [random]
 * @returns {number}
 */
function coneSpreadAngle(pelletSpreadDegrees, random = Math.random) {
  const centered = (random() + random()) / 2;
  const randomFactor = (centered - 0.5) * 2;
  return randomFactor * pelletSpreadDegrees * (Math.PI / 180);
}

/**
 * Config files have historically spelled modes as 'FULL_AUTO' while the enum is
 * 'full_auto'; both sides must agree or the server treats a full-auto weapon as
 * semi-auto (and rate-limits it). Normalize once, here.
 */
function normalizeFireMode(value) {
  const v = String(value ?? WeaponFireMode.SEMI_AUTO).toLowerCase();
  return Object.values(WeaponFireMode).includes(v) ? v : WeaponFireMode.SEMI_AUTO;
}

/**
 * Projectile types.
 *
 * Only BULLET has behaviour. LASER/ENERGY/ROCKET/PLASMA used to be listed here
 * and were treated exactly like bullets wherever they appeared, which made the
 * enum a promise the code did not keep. Add one back when something actually
 * flies differently (FU-065).
 */
const ProjectileType = {
  BULLET: 'bullet'
};

class Weapon extends Item {
  constructor(config) {
    // Set item type to weapon - stackable for upgrades, maxStackSize from config
    const maxStacks = config.maxStacks || 4;
    super({ ...config, type: ItemType.WEAPON, isEquippable: true, isStackable: true, maxStackSize: maxStacks });

    // Weapon-specific properties
    this.fireMode = normalizeFireMode(config.fireMode);
    this.fireRate = config.fireRate || 200;                    // MS between shots (base)
    this.damage = config.damage || 25;                         // Base damage

    // Burst shape. Meaningless for the other fire modes, but always present so
    // WeaponSystem's fireModes table can read it without a guard.
    this.burstCount = config.burstCount || 3;                  // Shots per trigger pull
    this.burstDelayMs = config.burstDelayMs || 60;             // MS between shots within a burst

    // Melee-only stats. Meaningless for every other fire mode, but always
    // present so MeleeSystem can read them without a guard.
    this.swingCooldownMs = config.swingCooldownMs || 500;
    this.meleeRange = config.meleeRange || 75;
    // Extra px added to meleeRange when resolving hits (reach tuning knob).
    this.meleeReachBonus = config.meleeReachBonus || 0;
    this.meleeArcDegrees = config.meleeArcDegrees || 110;

    // Stacking system - bonuses per stack level
    this.maxStacks = maxStacks;
    this.stackBonuses = config.stackBonuses || {
      damageIncrease: 0.15,        // +15% damage per stack (multiplicative)
      fireRateReduction: 0.08,     // -8% fire rate per stack (faster shooting)
      reloadReduction: 0.10,       // -10% reload time per stack
      rangeIncrease: 0.12          // +12% projectile lifetime per stack
    };

    // Ammo system. The reserve is the owner's pouch (Player.ammo[ammoType]),
    // shared by every gun of the same type; a weapon only knows its magazine.
    this.magCapacity = config.magCapacity || 30;
    this.ammoType = config.ammoType || null;
    this.pickupAmmo = Number.isFinite(config.pickupAmmo) ? config.pickupAmmo : 0;
    this.reloadTime = config.reloadTime || 2000;               // MS to reload (base)
    this.infiniteAmmo = config.infiniteAmmo || false;
    
    // Incremental reload system (for shotguns)
    this.reloadType = config.reloadType || 'full';             // 'full' or 'incremental'
    this.reloadPerRound = config.reloadPerRound || 500;        // MS per round for incremental reload

    // Multi-projectile system (for shotguns)
    this.pelletCount = config.pelletCount || 1;                // Number of projectiles per shot
    this.pelletDamage = config.pelletDamage || null;           // Damage per pellet (overrides base damage)
    this.pelletSpread = config.pelletSpread || 0;              // Spread angle for multi-projectile weapons
    this.spreadPattern = config.spreadPattern || 'random';     // 'random', 'uniform', or 'cone'

    // Weapon visual properties
    this.weaponSprite = config.weaponSprite || null;           // Path to weapon image (fallback/default)
    this.weaponSpriteIdle = config.weaponSpriteIdle || config.weaponSprite || null;  // Idle state (held at rest)
    this.weaponSpriteFiring = config.weaponSpriteFiring || config.weaponSprite || null; // Firing state (aimed/shooting)
    this.weaponWidth = config.weaponWidth || 40;               // Weapon sprite width
    this.weaponHeight = config.weaponHeight || 10;             // Weapon sprite height
    this.flipVertical = config.flipVertical || false;          // Flip sprite vertically when rendering
    this.gripOffset = config.gripOffset || { x: -10, y: 0 };   // Where player "holds" weapon (idle)
    this.gripOffsetIdle = config.gripOffsetIdle || config.gripOffset || { x: -10, y: 0 };   // Grip when idle
    this.gripOffsetFiring = config.gripOffsetFiring || config.gripOffset || { x: -10, y: 0 }; // Grip when firing
    this.muzzleOffset = config.muzzleOffset || { x: 20, y: 0 }; // Where bullets spawn from weapon
    this.firingStateDuration = config.firingStateDuration || 200; // MS to show firing sprite after shot

    // Projectile properties
    this.projectileType = config.projectileType || ProjectileType.BULLET;
    this.projectileSpeed = config.projectileSpeed || 1600;
    this.projectileLifetime = config.projectileLifetime || 3000;
    this.projectileSize = config.projectileSize || 4;
    this.projectileSprite = config.projectileSprite || null;   // Optional custom projectile sprite
    this.projectileWidth = config.projectileWidth || 8;        // Projectile sprite width
    this.projectileHeight = config.projectileHeight || 8;      // Projectile sprite height
    this.projectileRotates = config.projectileRotates || false; // Rotate projectile with trajectory

    // Spread/accuracy
    this.spread = config.spread || 0;                          // Degrees of random spread
    this.recoil = config.recoil || 0;                          // Recoil intensity

    // Visual effects
    this.muzzleFlash = config.muzzleFlash || null;             // Muzzle flash sprite
    this.tracerColor = config.tracerColor || '#ffff00';        // Projectile color

    // Damage falloff configuration
    this.falloff = config.falloff || null;                     // Distance-based damage falloff config

    // Weapon sounds (uses Item's sound system)
    // Expected events: 'fire', 'reload', 'empty', 'equip', 'unequip'
  }

  /**
   * Check if weapon can fire
   * @param {Object} weaponState - Current weapon state { currentAmmo, reloading, lastShotTime }
   * @param {number} currentTime - Current game time
   */
  canFire(weaponState, currentTime, options = {}) {
    if (weaponState.reloading) return false;
    if (!this.infiniteAmmo && weaponState.currentAmmo <= 0) return false;
    // The shots within one burst are spaced by burstDelayMs, which is shorter
    // than the weapon's between-trigger-pull rate, so they waive this gate.
    if (options.ignoreFireRate) return true;
    if (weaponState.lastShotTime && currentTime - weaponState.lastShotTime < this.fireRate) return false;
    return true;
  }

  /**
   * Fire the weapon
   * @param {Object} weaponState - Mutable weapon state to update
   * @param {number} currentTime - Current game time
   * @returns {Object|Object[]|null} - Projectile data, array of projectile data, or null
   */
  fire(weaponState, currentTime, options = {}) {
    if (!this.canFire(weaponState, currentTime, options)) {
      return null;
    }

    // Update weapon state
    weaponState.lastShotTime = currentTime;
    if (!this.infiniteAmmo) {
      weaponState.currentAmmo--;
    }

    const offsets = spreadOffsets(this, options.spreadSeed ?? null);

    // Multi-projectile weapons (shotguns)
    if (this.pelletCount > 1) {
      const projectiles = [];
      const damagePerPellet = this.pelletDamage || (this.damage / this.pelletCount);

      for (let i = 0; i < this.pelletCount; i++) {
        projectiles.push({
          type: this.projectileType,
          speed: this.projectileSpeed,
          lifetime: this.projectileLifetime,
          size: this.projectileSize,
          damage: damagePerPellet,
          sprite: this.projectileSprite,
          spriteWidth: this.projectileWidth,
          spriteHeight: this.projectileHeight,
          rotates: this.projectileRotates,
          color: this.tracerColor,
          spreadAngle: offsets[i],
          weaponId: this.id,
          pelletIndex: i  // Track which pellet this is
        });
      }

      return projectiles;
    }

    // Single projectile weapons
    const spreadAngle = offsets[0];

    // Return projectile config
    return {
      type: this.projectileType,
      speed: this.projectileSpeed,
      lifetime: this.projectileLifetime,
      size: this.projectileSize,
      damage: this.damage,
      sprite: this.projectileSprite,
      spriteWidth: this.projectileWidth,
      spriteHeight: this.projectileHeight,
      rotates: this.projectileRotates,
      color: this.tracerColor,
      spreadAngle: spreadAngle,
      weaponId: this.id  // Include weapon ID for client-side sprite lookup
    };
  }

  /**
   * Rounds available to reload from.
   * @param {Object} weaponState
   * @param {Record<string, number>|null|undefined} ammoPool the owner's pouch
   * @returns {number} Infinity in unlimited-ammo modes
   */
  reserveIn(weaponState, ammoPool) {
    if (weaponState?.unlimitedAmmo) return Infinity;
    if (!this.ammoType || !ammoPool) return 0;
    return Math.max(0, ammoPool[this.ammoType] || 0);
  }

  /**
   * Start reload
   * @param {Object} weaponState - Mutable weapon state to update
   * @param {number} currentTime - Current game time
   * @param {Record<string, number>|null|undefined} ammoPool - The owner's pouch
   */
  startReload(weaponState, currentTime, ammoPool) {
    if (weaponState.reloading) return false;
    if (this.infiniteAmmo) return false;
    if (weaponState.currentAmmo >= this.magCapacity) return false;

    if (this.reserveIn(weaponState, ammoPool) <= 0) return false;

    weaponState.reloading = true;
    weaponState.reloadStartTime = currentTime;
    
    // Calculate and store the actual reload duration for this reload
    if (this.reloadType === 'incremental') {
      const roundsNeeded = this.magCapacity - weaponState.currentAmmo;
      weaponState.reloadTime = (this.reloadPerRound || 500) * roundsNeeded;
    } else {
      weaponState.reloadTime = this.reloadTime;
    }
    
    return true;
  }

  /**
   * Complete reload (called after reload time elapses)
   * @param {Object} weaponState - Mutable weapon state to update
   * @param {Record<string, number>|null|undefined} ammoPool - The owner's pouch
   */
  completeReload(weaponState, ammoPool) {
    if (!weaponState.reloading) return;

    const ammoNeeded = this.magCapacity - weaponState.currentAmmo;
    const ammoToReload = Math.min(ammoNeeded, this.reserveIn(weaponState, ammoPool));

    weaponState.currentAmmo += ammoToReload;
    if (!weaponState.unlimitedAmmo && ammoToReload > 0) {
      ammoPool[this.ammoType] -= ammoToReload;
    }

    weaponState.reloading = false;
    weaponState.reloadStartTime = null;
    weaponState.reloadTime = null;
    weaponState.lastRoundLoadedTime = null;
  }

  /**
   * Add one round during incremental reload (for shotguns)
   * @param {Object} weaponState - Mutable weapon state to update
   * @param {number} currentTime - Current game time
   * @param {Record<string, number>|null|undefined} ammoPool - The owner's pouch
   * @returns {boolean} - True if round was added, false if reload is complete
   */
  addRound(weaponState, currentTime, ammoPool) {
    if (!weaponState.reloading) return false;
    if (weaponState.currentAmmo >= this.magCapacity) {
      // Magazine full, complete reload
      this.completeReload(weaponState, ammoPool);
      return false;
    }

    if (this.reserveIn(weaponState, ammoPool) <= 0) {
      // No more ammo, complete reload
      this.completeReload(weaponState, ammoPool);
      return false;
    }

    // Add one round
    weaponState.currentAmmo += 1;
    if (!weaponState.unlimitedAmmo) {
      ammoPool[this.ammoType] -= 1;
    }
    weaponState.lastRoundLoadedTime = currentTime;
    
    return true;
  }

  /**
   * Interrupt/cancel an ongoing reload
   * @param {Object} weaponState - Mutable weapon state to update
   * @returns {boolean} - True if reload was interrupted, false if wasn't reloading
   */
  interruptReload(weaponState) {
    if (!weaponState.reloading) return false;
    
    weaponState.reloading = false;
    weaponState.reloadStartTime = null;
    weaponState.reloadTime = null;
    weaponState.lastRoundLoadedTime = null;
    return true;
  }

  /**
   * Create a default weapon state
   */
  createState() {
    return {
      currentAmmo: this.magCapacity,
      reloading: false,
      reloadStartTime: null,
      reloadTime: null,  // Actual time for current reload
      lastShotTime: 0,
      unlimitedAmmo: false,
      // Incremental reload tracking
      lastRoundLoadedTime: null  // For incremental reloading
    };
  }

  // ─────────────────────────────────────────────────────────────
  // STACK BONUS CALCULATIONS
  // Stack 1 = base stats, each additional stack applies bonuses
  // ─────────────────────────────────────────────────────────────

  /**
   * Get damage at a given stack level
   * Each stack after 1 multiplies damage by (1 + damageIncrease)
   * @param {number} stacks - Current stack count (1-maxStacks)
   * @returns {number} - Calculated damage
   */
  getDamageMultiplierAtStack(stacks = 1) {
    const validStacks = Math.max(1, Math.min(stacks, this.maxStacks));
    const bonus = this.stackBonuses.damageIncrease || 0;
    // Stack 1 = base, each additional stack adds bonus
    return Math.pow(1 + bonus, validStacks - 1);
  }

  getDamageAtStack(stacks = 1) {
    return Math.round(this.damage * this.getDamageMultiplierAtStack(stacks));
  }

  /**
   * Get fire rate (ms between shots) at a given stack level
   * Lower is faster - each stack reduces fire rate
   * @param {number} stacks - Current stack count (1-maxStacks)
   * @returns {number} - Calculated fire rate in ms
   */
  getFireRateAtStack(stacks = 1) {
    const validStacks = Math.max(1, Math.min(stacks, this.maxStacks));
    const reduction = this.stackBonuses.fireRateReduction || 0;
    // Stack 1 = base, each additional stack reduces time between shots
    const multiplier = Math.pow(1 - reduction, validStacks - 1);
    return Math.round(this.fireRate * multiplier);
  }

  /**
   * Get reload time at a given stack level
   * Lower is faster - each stack reduces reload time
   * @param {number} stacks - Current stack count (1-maxStacks)
   * @returns {number} - Calculated reload time in ms
   */
  getReloadTimeAtStack(stacks = 1) {
    const validStacks = Math.max(1, Math.min(stacks, this.maxStacks));
    const reduction = this.stackBonuses.reloadReduction || 0;
    // Stack 1 = base, each additional stack reduces reload time
    const multiplier = Math.pow(1 - reduction, validStacks - 1);
    return Math.round(this.reloadTime * multiplier);
  }

  /**
   * Get projectile lifetime (range) at a given stack level
   * Higher is longer range - each stack increases lifetime
   * @param {number} stacks - Current stack count (1-maxStacks)
   * @returns {number} - Calculated projectile lifetime in ms
   */
  getProjectileLifetimeAtStack(stacks = 1) {
    const validStacks = Math.max(1, Math.min(stacks, this.maxStacks));
    const increase = this.stackBonuses.rangeIncrease || 0;
    // Stack 1 = base, each additional stack increases range
    const multiplier = Math.pow(1 + increase, validStacks - 1);
    return Math.round(this.projectileLifetime * multiplier);
  }

  /**
   * Get all stats at a given stack level (for UI display)
   * @param {number} stacks - Current stack count
   * @returns {Object} - All calculated stats
   */
  getStatsAtStack(stacks = 1) {
    return {
      damage: this.getDamageAtStack(stacks),
      fireRate: this.getFireRateAtStack(stacks),
      reloadTime: this.getReloadTimeAtStack(stacks),
      projectileLifetime: this.getProjectileLifetimeAtStack(stacks),
      stacks: stacks,
      maxStacks: this.maxStacks
    };
  }
}


export { Weapon, WeaponFireMode, ProjectileType, normalizeFireMode, coneSpreadAngle };
